import React from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Button, Flex, Text, Tooltip } from 'core/components';

import KaizenChatSession from 'app/views/core/kaizen/KaizenChatSession';
import { AiOutlineArrowLeft, AiOutlineClose, AiOutlineExpand } from 'react-icons/ai';
import { withRouter } from 'react-router-dom';

import KaizenChatList from 'app/views/core/kaizen/KaizenChatList';
import { InputGroup } from 'core/form';
import { FiPlus } from 'react-icons/fi';
import { BiHistory } from 'react-icons/bi';
import JourneysCopilotInput from './JourneysCopilotInput';
import JourneysCopilotSuggestions from './JourneysCopilotSuggestions';
import {
  AnimatedCard,
  COPILOT_CONFIG,
  ResizeHandle,
  ScrollContainer,
  ScrollToBottomButton
} from './JourneysCopilot.styles';

const DEFAULT_SUGGESTIONS = ['Whats going on with my network?', 'Summarize active alerts', 'Help me with this page'];

const TOOLTIP_CONFIG = {
  position: 'top',
  boundary: 'viewport',
  usePortal: true
};

@inject('$journeys')
@withRouter
@observer
class JourneysCopilot extends React.Component {
  state = {
    value: '',
    isScrolledToBottom: true,
    isExistingChatLoading: false,
    isShowingChatSelection: false,
    width: COPILOT_CONFIG.DIMENSIONS.DEFAULT_WIDTH,
    height: COPILOT_CONFIG.DIMENSIONS.DEFAULT_HEIGHT,
    isResizing: false,
    resizeType: null,
    showScrollToBottomButton: false,
    searchInputValue: ''
  };

  scrollAreaRef = React.createRef();

  updatePageContext = (pathname) => {
    const { $journeys } = this.props;

    // Remove /v4/ prefix from pathname for cleaner context
    const cleanPathname = pathname.replace(/^\/v4\//, '');

    // Add or update the page URL context
    $journeys.addChatContext({
      type: 'page',
      name: 'page:current',
      context: `Current page URL: ${cleanPathname}`,
      hidden: true,
      simple: true
    });
  };

  componentDidMount() {
    const { $journeys } = this.props;
    $journeys.chatSessionCollection.fetch();

    // Add scroll event listener
    if (this.scrollAreaRef.current) {
      this.scrollAreaRef.current.addEventListener('scroll', this.handleScroll);
      // Initial scroll check
      setTimeout(this.handleScroll, 100);
    }
  }

  componentWillUnmount() {
    this.removeResizeListeners();

    // Clean up scroll event listener
    if (this.scrollAreaRef.current) {
      this.scrollAreaRef.current.removeEventListener('scroll', this.handleScroll);
    }
  }

  // convert to px
  parseDimension = (value) => {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      if (value.endsWith('vw')) {
        return (parseFloat(value) / 100) * window.innerWidth;
      }
      if (value.endsWith('vh')) {
        return (parseFloat(value) / 100) * window.innerHeight;
      }
      if (value.endsWith('px')) {
        return parseFloat(value);
      }
    }

    return typeof value === 'number' ? value : null;
  };

  get presetDimensions() {
    const { $journeys } = this.props;

    const presetWidth = this.parseDimension($journeys.copilotDimensions.width);
    const presetHeight = this.parseDimension($journeys.copilotDimensions.height);

    return {
      presetWidth: presetWidth && Math.max(presetWidth, COPILOT_CONFIG.DIMENSIONS.MIN_WIDTH),
      presetHeight: presetHeight && Math.max(presetHeight, COPILOT_CONFIG.DIMENSIONS.MIN_HEIGHT)
    };
  }

  addResizeListeners = () => {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  };

  removeResizeListeners = () => {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  };

  // does not clear the chat session, just minimizes the UI
  handleMinimize = () => {
    const { $journeys } = this.props;
    $journeys.minimizeCopilot();
  };

  handleToggleChatSelection = () => {
    const { $journeys } = this.props;

    $journeys.chatSessionCollection?.clearFilters();

    this.setState((prevState) => ({
      isShowingChatSelection: !prevState.isShowingChatSelection,
      searchInputValue: ''
    }));
  };

  handleNavigateToJourney = () => {
    const { $journeys, history } = this.props;
    const id = $journeys.chatSessionCollection?.selected?.get('id');
    history.push(`${$journeys.baseUrl}/${id}`);
    $journeys.closeCopilot();
  };

  handleSend = (value) => {
    const { $journeys, location } = this.props;

    this.updatePageContext(location.pathname);

    $journeys.sendMessage(value);
    this.setState({ value: '' });
    this.scrollToBottom();
  };

  handleSuggestionClick = (suggestion) => {
    this.setState({ value: suggestion });
    this.handleSend(suggestion);
  };

  handleStartNewChat = () => {
    const { $journeys } = this.props;

    // don't need to do anything else here other than clear the selection
    // `handleSend` will handle creating a new journey
    $journeys.chatSessionCollection.clearSelection();
  };

  handleSelectJourney = (journey) => {
    this.setState({ isExistingChatLoading: true, isShowingChatSelection: false });
    journey.selectAndSubscribe({ fetch: true }).then(() => {
      this.setState({ isExistingChatLoading: false });
    });
  };

  scrollToBottom = () => {
    if (this.scrollAreaRef?.current) {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        const element = this.scrollAreaRef.current;
        if (element) {
          element.scrollTo({
            top: element.scrollHeight,
            behavior: 'smooth'
          });
          // Check scroll position after scrolling
          setTimeout(this.handleScroll, 100);
        }
      });
    }
  };

  handleMouseDown = (e, resizeType) => {
    const { $journeys } = this.props;
    e.preventDefault();
    e.stopPropagation();
    this.addResizeListeners();

    const { presetWidth, presetHeight } = this.presetDimensions;

    this.setState((prevState) => ({
      isResizing: true,
      resizeType,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: presetWidth || prevState.width,
      startHeight: presetHeight || prevState.height,
      width: presetWidth || prevState.width,
      height: presetHeight || prevState.height
    }));
    // clear preset dimensions so user control takes over
    $journeys.clearCopilotDimensions();
  };

  calculateNewDimensions = (deltaX, deltaY, resizeType, startWidth, startHeight) => {
    let newWidth = startWidth;
    let newHeight = startHeight;

    // Calculate max dimensions based on viewport
    const maxWidth = window.innerWidth - COPILOT_CONFIG.POSITION.RIGHT * 2;
    const maxHeight = window.innerHeight - COPILOT_CONFIG.POSITION.BOTTOM * 2;

    if (resizeType === 'left' || resizeType === 'top-left') {
      newWidth = Math.max(COPILOT_CONFIG.DIMENSIONS.MIN_WIDTH, Math.min(maxWidth, startWidth - deltaX));
    }

    if (resizeType === 'top' || resizeType === 'top-left') {
      newHeight = Math.max(COPILOT_CONFIG.DIMENSIONS.MIN_HEIGHT, Math.min(maxHeight, startHeight - deltaY));
    }

    return { newWidth, newHeight };
  };

  handleMouseMove = (e) => {
    const { isResizing, resizeType, startX, startY, startWidth, startHeight } = this.state;

    if (!isResizing) {
      return;
    }

    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;

    const { newWidth, newHeight } = this.calculateNewDimensions(deltaX, deltaY, resizeType, startWidth, startHeight);

    this.setState({ width: newWidth, height: newHeight });
  };

  handleMouseUp = () => {
    const { isResizing } = this.state;
    if (isResizing) {
      this.removeResizeListeners();
      this.setState({
        isResizing: false,
        resizeType: null,
        startX: undefined,
        startY: undefined,
        startWidth: undefined,
        startHeight: undefined
      });
    }
  };

  handleScroll = () => {
    if (!this.scrollAreaRef.current) {
      return;
    }

    const container = this.scrollAreaRef.current;
    const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50; // 50px threshold

    this.setState({ showScrollToBottomButton: !isAtBottom });
  };

  handleScrollToBottomClick = () => {
    this.scrollToBottom();
  };

  renderChatSelection = () => {
    const { $journeys } = this.props;

    if (!$journeys.chatSessionCollection) {
      return null;
    }

    return (
      <Flex flexDirection="column" gap={2} overflow="auto" bg="cardBackground" p="12px" height="100%">
        <KaizenChatList
          title="Recent"
          isCopilotMode
          onSelectJourney={this.handleSelectJourney}
          hideUserName
          hideShareLevel
          hideEditedDate={false}
          journeys={$journeys.chatSessionCollection.userChats}
          maxInitialItems={10}
        />
        <KaizenChatList
          title="Company"
          isCopilotMode
          onSelectJourney={this.handleSelectJourney}
          hideShareLevel
          hideEditedDate={false}
          journeys={$journeys.chatSessionCollection.companyChats}
          maxInitialItems={10}
        />
      </Flex>
    );
  };

  // Memoized calculations
  getSuggestions = () => {
    const { $journeys } = this.props;
    const suggestions = [...DEFAULT_SUGGESTIONS];

    if ($journeys.activeChatContext.size > 0) {
      suggestions.push(...Array.from($journeys.activeChatContext).flatMap((context) => context.suggestions));
    }

    return suggestions;
  };

  getTitle = (selectedChatSession) => {
    const sessionTitle = selectedChatSession?.chatSessionTitle;
    return sessionTitle && sessionTitle !== 'AI Advisor' && sessionTitle !== '' ? sessionTitle : 'AI Advisor';
  };

  handleSearchInputChange = (e) => {
    const { $journeys } = this.props;
    const { value = '' } = e.target;
    this.setState({ searchInputValue: value });
    $journeys.chatSessionCollection.searchChats(value);
  };

  handleTransitionEnd = (e) => {
    const { $journeys } = this.props;

    // Only reset on opacity transition completion when closing
    if (e.propertyName === 'opacity' && !$journeys.isCopilotOpen) {
      $journeys.clearCopilotDimensions();
    }
  };

  render() {
    const { $journeys } = this.props;
    const {
      value,
      isScrolledToBottom,
      isExistingChatLoading,
      isShowingChatSelection,
      width,
      height,
      showScrollToBottomButton,
      searchInputValue
    } = this.state;

    const { presetWidth, presetHeight } = this.presetDimensions;
    const suggestions = this.getSuggestions();
    const selectedChatSession = $journeys.chatSessionCollection?.selected;
    const hasMessages = selectedChatSession?.get('messages')?.length > 0;
    const title = this.getTitle(selectedChatSession);

    return (
      <AnimatedCard
        className={$journeys.isCopilotOpen ? 'visible' : 'hidden'}
        elevation={4}
        style={{
          width: `${presetWidth || width}px`,
          height: `${presetHeight || height}px`
        }}
        onTransitionEnd={this.handleTransitionEnd}
      >
        <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <ResizeHandle className="resizer resize-top" onMouseDown={(e) => this.handleMouseDown(e, 'top')} />
          <ResizeHandle className="resizer resize-left" onMouseDown={(e) => this.handleMouseDown(e, 'left')} />
          <ResizeHandle className="resizer resize-top-left" onMouseDown={(e) => this.handleMouseDown(e, 'top-left')} />

          <Flex alignItems="center" justifyContent="space-between" p={2} borderBottom="thin">
            <Flex alignItems="center" gap="8px">
              {isShowingChatSelection && (
                <>
                  <Button
                    icon={AiOutlineArrowLeft}
                    onClick={this.handleToggleChatSelection}
                    text="Back"
                    tracker="ai-advisor-button-back"
                  />
                </>
              )}
              {!isShowingChatSelection && <Text fontWeight="bold">{title}</Text>}
            </Flex>
            <Flex alignItems="center" gap="8px">
              {selectedChatSession && !isShowingChatSelection && (
                <>
                  <Tooltip content="New conversation" {...TOOLTIP_CONFIG}>
                    <Button
                      minimal
                      icon={FiPlus}
                      onClick={this.handleStartNewChat}
                      tracker="ai-advisor-button-new-chat"
                    />
                  </Tooltip>
                  <Box width="1px" height="20px" bg="#e1e5e9" />
                </>
              )}

              {isShowingChatSelection && (
                <InputGroup
                  autoFocus
                  leftIcon="search"
                  placeholder="Search..."
                  value={searchInputValue}
                  onChange={this.handleSearchInputChange}
                  type="search"
                />
              )}

              <Tooltip content="View history" disabled={isShowingChatSelection} {...TOOLTIP_CONFIG}>
                <Button
                  minimal
                  tracker="ai-advisor-button-toggle-history"
                  icon={BiHistory}
                  onClick={this.handleToggleChatSelection}
                  disabled={isShowingChatSelection}
                />
              </Tooltip>

              {hasMessages && selectedChatSession && (
                <Tooltip content="Go to full page Journey" disabled={isShowingChatSelection} {...TOOLTIP_CONFIG}>
                  <Button
                    minimal
                    icon={AiOutlineExpand}
                    tracker="ai-advisor-button-goto-full-page"
                    disabled={isShowingChatSelection}
                    onClick={this.handleNavigateToJourney}
                  />
                </Tooltip>
              )}

              <Tooltip content="Close" {...TOOLTIP_CONFIG}>
                <Button minimal icon={AiOutlineClose} onClick={this.handleMinimize} tracker="ai-advisor-button-close" />
              </Tooltip>
            </Flex>
          </Flex>

          <ScrollContainer
            flexDirection="column"
            justifyContent="flex-end"
            ref={this.scrollAreaRef}
            flex="1"
            overflowY="auto"
            overflowX="hidden"
            bg="decks.background"
          >
            {isShowingChatSelection && this.renderChatSelection()}

            {!isShowingChatSelection && hasMessages && (
              <KaizenChatSession
                isCopilotMode
                chatSessionModel={selectedChatSession}
                scrollToBottom={this.scrollToBottom}
                isScrolledToBottom={isScrolledToBottom}
                isExistingChatLoading={isExistingChatLoading}
              />
            )}

            {!isShowingChatSelection && !hasMessages && (
              <JourneysCopilotSuggestions
                suggestions={suggestions}
                onSuggestionClick={this.handleSuggestionClick}
                activeChatContext={$journeys.activeChatContext}
              />
            )}
          </ScrollContainer>

          <ScrollToBottomButton $isVisible={showScrollToBottomButton}>
            <Button icon="chevron-down" onClick={this.handleScrollToBottomClick} />
          </ScrollToBottomButton>

          <Box p={2} borderTop="thin">
            <JourneysCopilotInput
              value={value}
              onSubmit={this.handleSend}
              onRemoveContext={$journeys.removeChatContext}
              activeChatContext={$journeys.activeChatContext}
              isOpen={$journeys.isCopilotOpen}
            />
          </Box>
        </div>
      </AnimatedCard>
    );
  }
}

export default JourneysCopilot;
